<template>
  <div class="api-list">
    <Loading
      :active.sync="isLoading"
      :is-full-page="false"
      :color="loaderColor"
      loader="dots"
      :background-color="loaderBackgroundColor"
    />
    <div class="section-header">
      <h3 class="section-title"></h3>
      <div class="btns-container">
        <md-menu
          md-align-trigger
          md-size="big"
          md-direction="bottom-end"
          :mdCloseOnSelect="true"
          :mdCloseOnClick="true"
        >
          <md-button
            id="btn-integration-data"
            md-menu-trigger
            class="v-btn-primary"
            :disabled="false"
          >
            NEW
            <VasionArrowDropDownIcon />
          </md-button>
          <md-menu-content class="dropdown-list-container">
            <md-menu-item @click="newAttributeFormClick">
              Database Lookup
            </md-menu-item>
            <md-menu-item @click="newSalesForceFormClick">
              Salesforce
            </md-menu-item>
            <md-menu-item @click="newApiLookupClick"> API </md-menu-item>
          </md-menu-content>
        </md-menu>
      </div>
    </div>
    <div class="section-content">
      <div
        v-if="lookupsAvailable && tableData && !isLoading"
        class="vasion-html-table-default"
      >
        <VasionTable
          :headerColumns="ColumnValues"
          :hideColumns="hiddenColumns"
          :tableRows="tableData.Rows && tableData.Rows.Values"
          :filterByColumnDefault="1"
          :ellipsisButtonConfig="ellipsisButtonConfig"
          @ellipsis-button-clicked="ellipsisButtonClicked"
          @vasion-selection="rowClickHandler"
        />
      </div>
    </div>
    <md-dialog
      id="confirmLookupDelete"
      :md-active.sync="showDeleteDialog"
      :md-click-outside-to-close="false"
    >
      <VasionConfirmationDialog
        :message="deletePromptMessage"
        @noButtonClick="toggleDeleteDialog()"
        @yesButtonClick="deleteLookupAndToggle()"
      />
    </md-dialog>
    <VasionSnackbar
      id="snackbar-lookup-list"
      ref="lookupListSnackbar"
      :snackbarImage="snackbar.image"
      :showSnackbarBool.sync="snackbar.snackbar"
      :snackbarSubTitle="snackbar.subTitle"
      :snackbarTitle="snackbar.title"
    />
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay'
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'

export default {
  name: 'ApiLookupsList',
  components: {
    Loading,
  },
  data() {
    return {
      ColumnValues: [],
      deleteLookupId: 0,
      deleteLookupName: '',
      displayLookupSelection: false,
      formID: this.$route.params.formID,
      hiddenColumns: ['lookupID'],
      isLoading: true,
      loaderBackgroundColor: loaderBackgroundColor,
      loaderColor: loaderColor,
      tableData: {},
      selectedTabName: 'api',
      showDeleteDialog: false,
      snackbar: {
        image: '',
        snackbar: false,
        subTitle: '',
        title: '',
      },
    }
  },
  computed: {
    deletePromptMessage() {
      return `Are you sure you want to delete "${this.deleteLookupName}"?`
    },
    ellipsisButtonConfig() {
      return this.$store.state.security.ellipsisButtonConfig
    },
    lookupsAvailable() {
      return this.lookupList && this.lookupList.length > 0
    },
    lookupList() {
      return this.$store.state.fieldMapping.lookupList
    },
  },
  async created() {
    this.ColumnValues = [
      'lookupID',
      'Lookup Name',
      'Lookup Type',
      'Default Lookup',
      '_VasionEllipsisButton_',
    ]
    const payload = {
      includeSalesForce: true,
      objectId: this.$route.params.formID,
    }
    await this.$store.dispatch('fieldMapping/getDatabaseLookups', payload)
    this.refreshTable()
    this.isLoading = false
    await this.$store.dispatch(
      'security/setEllipsisButtonConfig',
      'field-mapping'
    )
  },
  methods: {
    newApiLookupClick() {
      this.$router.push({
        name: 'NewApiLookup',
        params: { formID: this.formID },
      })
    },
    calculatedClass(selectedTab) {
      return this.selectedTabName === selectedTab ? 'text-selected' : 'text'
    },
    async deleteLookupAndToggle() {
      if (this.deleteLookupId <= 0) {
        this.showSnackbar('Error', 'Invalid lookup ID.', false)
        return
      }

      try {
        const wasDeleted = await this.$store.dispatch(
          'fieldMapping/deleteDatabaseLookup',
          this.deleteLookupId
        )

        if (
          wasDeleted &&
          wasDeleted.data &&
          wasDeleted.data.Value === 'False'
        ) {
          const message =
            wasDeleted.data.ResultMessage ||
            'An unexpected error occurred. Please try again.'
          this.showSnackbar('Error', message, false)
          return
        }

        const payload = {
          includeSalesForce: true,
          objectId: this.$route.params.formID,
        }
        await this.$store.dispatch('fieldMapping/getDatabaseLookups', payload)
        await this.refreshTable()

        this.showSnackbar('Success', 'Lookup deleted successfully.', true)
      } catch (error) {
        console.error('Error during lookup deletion:', error)
        this.showSnackbar(
          'Error',
          'An unexpected error occurred. Please try again.',
          false
        )
      } finally {
        this.deleteLookupId = 0
        this.deleteLookupName = ''
        this.toggleDeleteDialog()
      }
    },
    async editLookup(lookupId, lookupType) {
      this.$store.dispatch('forms/setFromIntegrations', true)
      this.$store.dispatch('forms/setFormID', this.$route.params.formID)
      switch (lookupType) {
        case 'Database':
          this.$router.push({
            name: 'EditDatabaseLookup',
            params: { lookupId: lookupId },
          })
          break
        case 'SalesForce':
          this.$router.push({
            name: 'EditSalesForceLookup',
            params: { lookupId: lookupId },
          })
          break
        case 'Api':
          this.$router.push({
            name: 'EditApiLookup',
            params: { formID: this.formID, apiLookupId: lookupId },
          })
          break
      }
    },
    ellipsisButtonClicked(payload) {
      if (payload?.item?.Values[0]) {
        const [id, name] = payload.item.Values
        switch (payload.buttonId) {
          case 'edit-field-mapping-button':
            this.editLookup(id, payload.item.Values[2])
            break
          case 'delete-field-mapping-button':
            this.deleteMessage = `Are you sure you want to delete "${name}"?`
            this.setDeleteValues(id, name)
            break
          default:
            break
        }
      }
    },
    newAttributeFormClick() {
      this.$store.dispatch('forms/setFromIntegrations', true)
      this.$store.dispatch('forms/setFormID', this.$route.params.formID)
      this.$router.push({ name: 'EditDatabaseLookup', params: { lookupId: 0 } })
    },
    newSalesForceFormClick() {
      this.$store.dispatch('forms/setFromIntegrations', true)
      this.$store.dispatch('forms/setFormID', this.$route.params.formID)
      this.$router.push({
        name: 'EditSalesForceLookup',
        params: { lookupId: 0 },
      })
    },
    refreshTable() {
      this.tableData.Rows = {
        Values: [],
      }

      this.tableData.Rows.Values = this.lookupList.map(lookupElement => {
        const dataRow = [
          lookupElement.id,
          lookupElement.name,
          lookupElement.lookupType,
          lookupElement.defaultLookup ? 'Yes' : 'No',
          'VasionEllipsisIcon',
        ]

        const retElement = {
          Values: dataRow,
          ValueType: 'String',
        }

        return retElement
      })
    },
    rowClickHandler(data) {
      if (!data || !data.Values || data.Values.length <= 0) return
      this.editLookup(data.Values[0], data.Values[2])
    },
    setDeleteValues(lookupId, lookupName) {
      this.deleteLookupId = lookupId
      this.deleteLookupName = lookupName
      this.toggleDeleteDialog()
    },
    showSnackbar(title, subTitle, isSuccess) {
      this.snackbar = {
        image: isSuccess,
        snackbar: true,
        subTitle: subTitle,
        title: title,
      }
    },
    toggleDeleteDialog() {
      this.showDeleteDialog = !this.showDeleteDialog
    },
    toggleLookupSelection() {
      this.displayLookupSelection = !this.displayLookupSelection
    },
    updateSelectedTab(newSelectedTab) {
      this.selectedTabName = newSelectedTab
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
@import '@/assets/css/light-theme-system-palette.scss';
@import '@/assets/css/btn-styles.scss';

.api-list {
  display: flex;
  height: 100%;
  flex: 1 1 auto;
  flex-direction: column;
  overflow: auto;
  padding: 12px;
}

.section-header {
  display: flex;
  height: 48px;
  align-items: flex-start;
  gap: 16px;

  .section-title {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1 0 0;
    margin: auto;

    overflow: hidden;
    color: $vap-temp-title-color;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: $font-family-book;
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: 32px;
  }
}

.section-content {
  flex: 1 1 auto;
  overflow: auto;
}
</style>
